import { GameState } from '../../../shared/types';

export interface BallPhysicsConfig {
  ballSpeed: number; // base speed in pixels per second
  ballSize: number; // ball size in pixels
  paddleWidth: number; // paddle width in pixels
  paddleHeight: number; // paddle height in pixels
  canvasWidth: number; // canvas width in pixels
  canvasHeight: number; // canvas height in pixels
  speedIncrement: number; // speed multiplier per paddle hit
}

export interface CollisionResult {
  scored: boolean;
  scoringPlayer: 1 | 2 | null;
  ballReset: boolean;
}

export class BallPhysics {
  private config: BallPhysicsConfig;

  constructor(config: BallPhysicsConfig) {
    this.config = config;
  }

  /**
   * Update ball position and handle all collisions using sub-stepping
   */
  updateBall(gameState: GameState, deltaTime: number): CollisionResult {
    const result: CollisionResult = {
      scored: false,
      scoringPlayer: null,
      ballReset: false
    };

    // Use sub-stepping to prevent tunneling through paddles
    // Divide the frame into multiple smaller steps
    const numSubSteps = 8; // Balanced number of sub-steps for accuracy without performance impact
    const subDeltaTime = deltaTime / numSubSteps;
    
    // For each sub-step
    for (let step = 0; step < numSubSteps; step++) {
      // Calculate movement for this sub-step (velocity is already in pixels per second)
      const subMoveX = gameState.ball.vx * subDeltaTime;
      const subMoveY = gameState.ball.vy * subDeltaTime;
      
      // Update ball position for this sub-step
      gameState.ball.x += subMoveX;
      gameState.ball.y += subMoveY;
      
      // Check wall collisions (top and bottom)
      this.handleWallCollisions(gameState);
      
      // Check paddle collisions
      const paddleCollision = this.checkPaddleCollision(gameState);
      
      // If paddle collision occurred, don't process any more sub-steps
      if (paddleCollision) {
        break;
      }
      
      // Check scoring collisions (left and right boundaries)
      const scoringResult = this.handleScoringCollisions(gameState);
      if (scoringResult.scored && scoringResult.scoringPlayer) {
        result.scored = true;
        result.scoringPlayer = scoringResult.scoringPlayer;
        result.ballReset = true;
        this.resetBall(gameState, scoringResult.scoringPlayer);
        break; // Stop sub-stepping if a score occurred
      }
    }

    return result;
  }

  /**
   * Handle collisions with top and bottom walls
   */
  private handleWallCollisions(gameState: GameState): void {
    const ballRadius = this.config.ballSize / 2;
    
    // Top wall collision
    if (gameState.ball.y - ballRadius <= 0) {
      gameState.ball.y = ballRadius;
      gameState.ball.vy = Math.abs(gameState.ball.vy); // Bounce down
    }
    
    // Bottom wall collision
    if (gameState.ball.y + ballRadius >= this.config.canvasHeight) {
      gameState.ball.y = this.config.canvasHeight - ballRadius;
      gameState.ball.vy = -Math.abs(gameState.ball.vy); // Bounce up
    }
  }

  /**
   * Paddle collision detection using AABB (Axis-Aligned Bounding Box) with pixel coordinates
   * Returns true if a collision was detected and handled
   */
  private checkPaddleCollision(gameState: GameState): boolean {
    const ballRadius = this.config.ballSize / 2;
    const ballX = gameState.ball.x;
    const ballY = gameState.ball.y;
    
    // Paddle positions (in pixels)
    const paddle1X = 20; // Left paddle X position (matches GameDisplay.tsx)
    const paddle1Y = gameState.paddles.player1.y;
    
    const paddle2X = this.config.canvasWidth - 30; // Right paddle X position (matches GameDisplay.tsx)
    const paddle2Y = gameState.paddles.player2.y;
    
    // AABB Collision Detection with pixel coordinates
    
    // Check for collision with Player 1 paddle (left side)
    if (gameState.ball.vx < 0) { // Only check if ball is moving left
      // Check if ball overlaps with paddle rectangle
      const ballLeft = ballX - ballRadius;
      const ballRight = ballX + ballRadius;
      const ballTop = ballY - ballRadius;
      const ballBottom = ballY + ballRadius;
      
      const paddleLeft = paddle1X;
      const paddleRight = paddle1X + this.config.paddleWidth;
      const paddleTop = paddle1Y;
      const paddleBottom = paddle1Y + this.config.paddleHeight;
      
      // AABB overlap test
      if (ballRight >= paddleLeft && ballLeft <= paddleRight &&
          ballBottom >= paddleTop && ballTop <= paddleBottom) {
        this.handlePaddleHit(gameState, paddle1Y, 'left');
        return true;
      }
    }
    
    // Check for collision with Player 2 paddle (right side)
    if (gameState.ball.vx > 0) { // Only check if ball is moving right
      // Check if ball overlaps with paddle rectangle
      const ballLeft = ballX - ballRadius;
      const ballRight = ballX + ballRadius;
      const ballTop = ballY - ballRadius;
      const ballBottom = ballY + ballRadius;
      
      const paddleLeft = paddle2X;
      const paddleRight = paddle2X + this.config.paddleWidth;
      const paddleTop = paddle2Y;
      const paddleBottom = paddle2Y + this.config.paddleHeight;
      
      // AABB overlap test
      if (ballRight >= paddleLeft && ballLeft <= paddleRight &&
          ballBottom >= paddleTop && ballTop <= paddleBottom) {
        this.handlePaddleHit(gameState, paddle2Y, 'right');
        return true;
      }
    }
    
    return false;
  }

  /**
   * Handle ball hitting a paddle
   */
  private handlePaddleHit(gameState: GameState, paddleY: number, side: 'left' | 'right'): void {
    // Calculate where on the paddle the ball hit (0 = top, 1 = bottom)
    const hitPosition = (gameState.ball.y - paddleY) / this.config.paddleHeight;
    const normalizedHit = Math.max(0, Math.min(1, hitPosition)); // Clamp to 0-1
    
    // Calculate new angle based on hit position
    // Center hit (0.5) = straight horizontal
    // Top hit (0) = angled up
    // Bottom hit (1) = angled down
    const maxAngleRadians = Math.PI / 4; // 45 degrees maximum angle
    const angle = (normalizedHit - 0.5) * 2 * maxAngleRadians; // Range: -45° to +45°
    
    // Calculate new velocity direction
    const speed = Math.sqrt(gameState.ball.vx * gameState.ball.vx + gameState.ball.vy * gameState.ball.vy);
    const speedMultiplier = 1 + this.config.speedIncrement;
    const newSpeed = speed * speedMultiplier;
    
    // Set new velocity based on angle and side
    if (side === 'left') {
      gameState.ball.vx = Math.cos(angle) * newSpeed; // Always positive (moving right)
      gameState.ball.vy = Math.sin(angle) * newSpeed;
    } else {
      gameState.ball.vx = -Math.cos(angle) * newSpeed; // Always negative (moving left)
      gameState.ball.vy = Math.sin(angle) * newSpeed;
    }
    
    // Move ball away from paddle to prevent multiple collisions
    const ballRadius = this.config.ballSize / 2;
    if (side === 'left') {
      gameState.ball.x = 20 + this.config.paddleWidth + ballRadius + 2; // Left paddle + width + ball radius + buffer
    } else {
      gameState.ball.x = (this.config.canvasWidth - 30) - ballRadius - 2; // Right paddle - ball radius - buffer
    }
  }

  /**
   * Handle scoring collisions (left and right boundaries)
   */
  private handleScoringCollisions(gameState: GameState): { scored: boolean; scoringPlayer: 1 | 2 | null } {
    const ballRadius = this.config.ballSize / 2;
    
    // Left boundary - Player 2 scores
    if (gameState.ball.x - ballRadius <= 0) {
      gameState.score.player2++;
      return { scored: true, scoringPlayer: 2 };
    }
    
    // Right boundary - Player 1 scores
    if (gameState.ball.x + ballRadius >= this.config.canvasWidth) {
      gameState.score.player1++;
      return { scored: true, scoringPlayer: 1 };
    }
    
    return { scored: false, scoringPlayer: null };
  }

  /**
   * Reset ball to center after scoring
   */
  private resetBall(gameState: GameState, lastScoringPlayer: 1 | 2): void {
    // Reset position to center (in pixels)
    gameState.ball.x = this.config.canvasWidth / 2;
    gameState.ball.y = this.config.canvasHeight / 2;
    
    // Serve ball toward the player who didn't score
    const serveDirection = lastScoringPlayer === 1 ? -1 : 1; // Serve away from scoring player
    
    // Random vertical angle (up to 30 degrees)
    const maxAngle = Math.PI / 6; // 30 degrees
    const randomAngle = (Math.random() - 0.5) * 2 * maxAngle;
    
    // Set velocity in pixels per second
    gameState.ball.vx = serveDirection * Math.cos(randomAngle) * this.config.ballSpeed;
    gameState.ball.vy = Math.sin(randomAngle) * this.config.ballSpeed;
  }

  /**
   * Initialize ball for game start
   */
  static initializeBall(gameState: GameState, config: BallPhysicsConfig): void {
    // Center position (in pixels)
    gameState.ball.x = config.canvasWidth / 2;
    gameState.ball.y = config.canvasHeight / 2;
    
    // Random initial direction
    const direction = Math.random() < 0.5 ? -1 : 1;
    const maxAngle = Math.PI / 6; // 30 degrees
    const randomAngle = (Math.random() - 0.5) * 2 * maxAngle;
    
    // Set velocity in pixels per second
    gameState.ball.vx = direction * Math.cos(randomAngle) * config.ballSpeed;
    gameState.ball.vy = Math.sin(randomAngle) * config.ballSpeed;
  }
}